import { Button, Icon, Label, ScrollArea, SegmentedButtons, Tooltip, token } from "@may-i/mash-design-system";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { CameraMemo, CameraMemoType } from "~/entities/camera-memo/types";
import type { Camera } from "~/entities/camera/types";
import type { MemoAction } from "~/pages/camera-mapping/MemoPanel";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { IcDelete } from "~/shared/assets/icons/IcDelete";
import { IcPickOnImg } from "~/shared/assets/icons/IcPickOnImg";
import { compareCamId } from "~/shared/lib/compare-cam-id";

interface AllMemosPanelProps {
  memos: CameraMemo[];
  cameras: Camera[];
  getDisplayName: (camera: Camera) => string;
  selectedCameraId: number | null;
  selectedMemoId?: number | null;
  /** 메모가 "현재 컨텍스트(도면/스냅샷)" 위에 배치되어 있는지 판별. 미지정 시 positionForFloorPlan 만 본다 */
  isPlacedOnContext?: (memo: CameraMemo) => boolean;
  onSave: (actions: MemoAction[]) => void;
  onClose: () => void;
  onMemoClick: (cameraId: number) => void;
  onMemoSelect?: (memoId: number | null) => void;
  onToggleMemoPlacement: (memo: CameraMemo) => void;
  placementLabels?: { show: string; remove: string };
  /** 헤더(타이틀 + 닫기 버튼) 노출 여부. 외부에 별도 탭/네비가 있을 때 false. 기본 true. */
  showHeader?: boolean;
}

interface LocalMemo {
  localId: string;
  memo: CameraMemo;
  content: string;
  type: CameraMemoType;
  isEditing: boolean;
}

export function AllMemosPanel({
  memos,
  cameras,
  getDisplayName,
  selectedCameraId,
  selectedMemoId,
  isPlacedOnContext,
  onSave,
  onClose,
  onMemoClick,
  onMemoSelect,
  onToggleMemoPlacement,
  placementLabels,
  showHeader = true,
}: AllMemosPanelProps) {
  const { t } = useTranslation("analysis-draft");
  const { t: tMemo } = useTranslation("camera-mapping");

  const [localMemos, setLocalMemos] = useState<LocalMemo[]>([]);

  useEffect(() => {
    setLocalMemos((prev) => {
      const prevMap = new Map(prev.map((lm) => [lm.localId, lm]));
      return memos
        .slice()
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((m) => {
          const localId = `server-${m.id}`;
          const existing = prevMap.get(localId);
          if (existing?.isEditing) {
            return { ...existing, memo: m };
          }
          return { localId, memo: m, content: m.content, type: m.type, isEditing: false };
        });
    });
  }, [memos]);

  const getCameraDisplayName = useCallback(
    (cameraId: number) => {
      const camera = cameras.find((c) => c.id === cameraId);
      if (!camera) return `Camera ${cameraId}`;
      return getDisplayName(camera);
    },
    [cameras, getDisplayName],
  );

  // Group memos by camera
  const groupedMemos = (() => {
    const groups: { cameraId: number; displayName: string; memos: LocalMemo[] }[] = [];
    const cameraMap = new Map<number, LocalMemo[]>();
    for (const lm of localMemos) {
      const arr = cameraMap.get(lm.memo.cameraId) ?? [];
      arr.push(lm);
      cameraMap.set(lm.memo.cameraId, arr);
    }
    for (const [cameraId, memoList] of cameraMap) {
      groups.push({
        cameraId,
        displayName: getCameraDisplayName(cameraId),
        memos: memoList,
      });
    }
    // Sort groups by camera display name
    groups.sort((a, b) => compareCamId(a.displayName, b.displayName));
    return groups;
  })();

  const handleContentChange = useCallback((localId: string, content: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) => (lm.localId === localId ? { ...lm, content } : lm)),
    );
  }, []);

  const handleTypeChange = useCallback((localId: string, type: CameraMemoType) => {
    setLocalMemos((prev) =>
      prev.map((lm) => (lm.localId === localId ? { ...lm, type } : lm)),
    );
  }, []);

  const handleStartEdit = useCallback((localId: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) => (lm.localId === localId ? { ...lm, isEditing: true } : lm)),
    );
  }, []);

  const handleCancelEdit = useCallback((localId: string) => {
    setLocalMemos((prev) =>
      prev.map((lm) =>
        lm.localId === localId
          ? { ...lm, content: lm.memo.content, type: lm.memo.type, isEditing: false }
          : lm,
      ),
    );
  }, []);

  const handleSaveMemo = useCallback((localId: string) => {
    const lm = localMemos.find((m) => m.localId === localId);
    if (!lm) return;
    if (lm.content === lm.memo.content && lm.type === lm.memo.type) return;
    onSave([{ actionType: "update", data: { ...lm.memo, content: lm.content, type: lm.type } }]);
    setLocalMemos((prev) =>
      prev.map((m) =>
        m.localId === localId
          ? { ...m, memo: { ...m.memo, content: lm.content, type: lm.type }, isEditing: false }
          : m,
      ),
    );
  }, [localMemos, onSave]);

  const [pendingDeleteLocalId, setPendingDeleteLocalId] = useState<string | null>(null);

  const handleDeleteMemo = useCallback((localId: string) => {
    const lm = localMemos.find((m) => m.localId === localId);
    if (!lm) return;
    // 영구 삭제는 확인 다이얼로그 (도면 위 메모 삭제와 일관성)
    setPendingDeleteLocalId(localId);
  }, [localMemos]);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDeleteLocalId) return;
    const lm = localMemos.find((m) => m.localId === pendingDeleteLocalId);
    if (lm) {
      onSave([{ actionType: "delete", data: lm.memo }]);
    }
    setPendingDeleteLocalId(null);
  }, [pendingDeleteLocalId, localMemos, onSave]);

  const hasMemoChanges = useCallback((lm: LocalMemo) => {
    return lm.content !== lm.memo.content || lm.type !== lm.memo.type;
  }, []);

  return (
    <Container>
      {showHeader && (
        <Header>
          <HeaderLeft>
            <Title>{t("allMemos.title")}</Title>
          </HeaderLeft>
          <Button variant="outlined" color="primary" size="xSmall" onClick={onClose}>
            {t("allMemos.close")}
          </Button>
        </Header>
      )}

      <MemoListWrapper>
        <ScrollArea>
          <MemoListInner>
            {groupedMemos.length === 0 && (
              <EmptyMessage>{t("allMemos.empty")}</EmptyMessage>
            )}
            {groupedMemos.map((group) => (
              <CameraGroup key={group.cameraId}>
                <CameraGroupHeader
                  $active={group.cameraId === selectedCameraId}
                  onClick={() => onMemoClick(group.cameraId)}
                >
                  <Icon icon="ic_video_on" width={14} height={14} />
                  <CameraGroupName>{group.displayName}</CameraGroupName>
                  <MemoCountBadge>{group.memos.length}</MemoCountBadge>
                </CameraGroupHeader>
                {group.memos.map((lm) => (
                  <MemoCard
                    key={lm.localId}
                    $isActive={lm.isEditing}
                    $isHighlighted={!lm.isEditing && lm.memo.id === selectedMemoId}
                    onClick={() => {
                      if (lm.isEditing) return;
                      onMemoClick(lm.memo.cameraId);
                      if (onMemoSelect) {
                        onMemoSelect(lm.memo.id === selectedMemoId ? null : lm.memo.id);
                      }
                    }}
                  >
                    <MemoCardHeader>
                      <MemoCardHeaderLeft>
                        {lm.isEditing ? (
                          <SegmentedButtons
                            size="xSmall"
                            variant="outlined"
                            color="primary"
                            menu={[
                              { value: "default", label: tMemo("memo.typeDefault") },
                              { value: "adjustmentOrAddition", label: tMemo("memo.typeAdjustment") },
                            ]}
                            value={lm.type}
                            onValueChange={(value) => {
                              if (value) handleTypeChange(lm.localId, value as CameraMemoType);
                            }}
                          />
                        ) : (
                          <>
                            <MemoTypeLabel
                              $type={lm.type}
                              variant={lm.type === "adjustmentOrAddition" ? "soft" : "filled"}
                              color="default"
                              size="xSmall"
                            >
                              {lm.type === "default" ? tMemo("memo.typeDefault") : tMemo("memo.typeAdjustment")}
                            </MemoTypeLabel>
                            <MetaSeparator />
                            <MetaText>
                              {new Date(lm.memo.updatedAt).toLocaleDateString("ko-KR", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              }).replace(/\. /g, ".").replace(/\.$/, "")}
                              {lm.memo.createdAt != null && lm.memo.createdAt !== lm.memo.updatedAt && ` ${tMemo("memo.modified")}`}
                            </MetaText>
                          </>
                        )}
                      </MemoCardHeaderLeft>
                      <MemoCardHeaderRight>
                        {lm.isEditing ? (
                          <>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="xSmall"
                              onClick={(e) => { e.stopPropagation(); handleCancelEdit(lm.localId); }}
                            >
                              {tMemo("memo.cancel")}
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="xSmall"
                              disabled={!hasMemoChanges(lm)}
                              onClick={(e) => { e.stopPropagation(); handleSaveMemo(lm.localId); }}
                            >
                              {tMemo("memo.save")}
                            </Button>
                          </>
                        ) : (
                          <>
                            {(() => {
                              const isPlaced = isPlacedOnContext
                                ? isPlacedOnContext(lm.memo)
                                : lm.memo.positionForFloorPlan != null;
                              const placementLabel = isPlaced
                                ? (placementLabels?.remove ?? tMemo("memo.removeFromFloorPlan"))
                                : (placementLabels?.show ?? tMemo("memo.showOnFloorPlan"));
                              return (
                                <Tooltip
                                  trigger={
                                    <SnapshotActionButton
                                      $active={isPlaced}
                                      onClick={(e) => { e.stopPropagation(); onMemoClick(lm.memo.cameraId); onToggleMemoPlacement(lm.memo); }}
                                      aria-label={placementLabel}
                                    >
                                      <IcPickOnImg width={14} height={14} />
                                    </SnapshotActionButton>
                                  }
                                  side="top"
                                  sideOffset={4}
                                >
                                  {placementLabel}
                                </Tooltip>
                              );
                            })()}
                            <Tooltip
                              trigger={
                                <IconActionButton
                                  onClick={(e) => { e.stopPropagation(); handleStartEdit(lm.localId); }}
                                  aria-label={tMemo("memo.edit")}
                                >
                                  <Icon icon="ic_edit" width={14} height={14} />
                                </IconActionButton>
                              }
                              side="top"
                              sideOffset={4}
                            >
                              {tMemo("memo.edit")}
                            </Tooltip>
                            <Tooltip
                              trigger={
                                <IconActionButton
                                  onClick={(e) => { e.stopPropagation(); handleDeleteMemo(lm.localId); }}
                                  aria-label={tMemo("memo.delete")}
                                >
                                  <IcDelete width={14} height={14} />
                                </IconActionButton>
                              }
                              side="top"
                              sideOffset={4}
                            >
                              {tMemo("memo.delete")}
                            </Tooltip>
                          </>
                        )}
                      </MemoCardHeaderRight>
                    </MemoCardHeader>
                    {lm.isEditing ? (
                      <MemoTextArea
                        placeholder={tMemo("memo.placeholder")}
                        value={lm.content}
                        onChange={(e) => handleContentChange(lm.localId, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <MemoContent>{lm.content}</MemoContent>
                    )}
                  </MemoCard>
                ))}
              </CameraGroup>
            ))}
          </MemoListInner>
        </ScrollArea>
      </MemoListWrapper>
      <DeleteConfirmDialog
        open={pendingDeleteLocalId !== null}
        title={tMemo("dialog.deleteMemoTitle")}
        name=""
        messageKey=""
        message={tMemo("dialog.deleteMemoMessage")}
        ns="camera-mapping"
        onCancel={() => setPendingDeleteLocalId(null)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
}

// --- Styles ---

const MemoTypeLabel = styled(Label)<{ $type: CameraMemoType }>`
  ${({ $type }) =>
    $type === "adjustmentOrAddition" &&
    `--label-color: #EB6227;
     --label-background-color: rgba(235, 98, 39, 0.16);`}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 12px 0 24px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
`;

const MemoListWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;

const MemoListInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 12px 100px 24px;
`;

const CameraGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CameraGroupHeader = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: ${token("colors.text.secondary")};

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(72, 75, 210, 0.08);
      color: #484BD2;
    `}

  &:hover {
    background: ${({ $active }) => $active ? "rgba(72, 75, 210, 0.12)" : "rgba(49, 62, 131, 0.08)"};
  }
`;

const CameraGroupName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
`;

const MemoCountBadge = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${token("colors.text.secondary")};
  background: rgba(143, 149, 178, 0.16);
  border-radius: 10px;
  padding: 2px 6px;
`;

const MemoCard = styled.div<{ $isActive?: boolean; $isHighlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;

  ${({ $isActive, $isHighlighted }) =>
    $isActive
      ? css`
          border: 1px solid #11111f;
          cursor: default;
        `
      : $isHighlighted
        ? css`
            border: 2px solid #484BD2;
          `
        : css`
            border: 1px solid rgba(143, 149, 178, 0.32);
            &:hover {
              border-color: rgba(143, 149, 178, 0.56);
            }
          `}
`;

const MemoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
`;

const MemoCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const MemoCardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MetaSeparator = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(143, 149, 178, 0.24);
  flex-shrink: 0;
`;

const MetaText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #696f8c;
  white-space: nowrap;
`;

const SnapshotActionButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  ${({ $active }) =>
    $active
      ? css`
          color: #484BD2;
          background: rgba(72, 75, 210, 0.08);
        `
      : css`
          color: #242946;
        `}

  &:hover {
    background: rgba(49, 62, 131, 0.08);
  }
`;

const IconActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: #242946;

  &:hover {
    background: rgba(49, 62, 131, 0.08);
  }
`;

const MemoTextArea = styled.textarea`
  width: 100%;
  min-height: 66px;
  padding: 8px 12px;
  border: none;
  outline: none;
  resize: none;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  background: transparent;

  &::placeholder {
    color: #8f95b2;
  }
`;

const MemoContent = styled.div`
  padding: 8px 12px;
  min-height: 66px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 12px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;
